import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { environment } from '../../../../environments/environment';
import {
  usercentricsAutoblockerScriptId,
  usercentricsAutoblockerSrc,
  usercentricsCmpScriptId,
  usercentricsCmpSrc,
  usercentricsConsentChangeEvent,
  usercentricsInitializedEvent
} from './usercentrics.constants';

@Injectable({ providedIn: 'root' })
export class UsercentricsService {
  private consentDecisionPromise: Promise<void> | null = null;
  private consentListenerAttached = false;
  private consentReadErrorLogged = false;
  private isEnabled = environment.usercentrics.enabled;
  private settingsId = environment.usercentrics.settingsId;

  constructor(@Inject(PLATFORM_ID) private platformId: Object) {}

  getConsentData(): any {
    if (!isPlatformBrowser(this.platformId)) {
      return null;
    }

    const ui = window.UC_UI;

    try {
      // V3 Web CMP (loader.js) on this build returns Promises from
      // getServicesFullInfo()/getServicesBaseInfo(), so we cannot read them
      // synchronously. The SDK persists consent under localStorage['ucData']
      // the moment the user accepts/denies. ucData is hydrated before UC_UI
      // signals initialised and survives reloads.
      
      const ucData = this.readUcData();

      let services: any[] | null = null;
      let gcm: any = null;

      if (ucData) {
        gcm = ucData.gcm ?? null;
        services = this.extractServicesArray(ucData);
      }

      if (!services && ui) {
        const raw = typeof ui.getServicesFullInfo === 'function'
          ? ui.getServicesFullInfo()
          : (typeof ui.getServicesBaseInfo === 'function' ? ui.getServicesBaseInfo() : null);
        if (raw && typeof (raw as any).then !== 'function') {
          if (Array.isArray(raw)) {
            services = raw;
          } else if (typeof raw === 'object') {
            services = Object.values(raw).filter((value) => value && typeof value === 'object');
          }
        }
      }

      if (!services && !gcm) {
        return null;
      }

      // Build a category-keyed consents map from gcm (preferred) and fall back
      // to per-service category aggregation when gcm is absent.
      const consents: Record<string, { status: boolean }> = {};

      if (gcm && typeof gcm === 'object') {
        const gcmGranted = (key: string) => gcm[key] === 'granted';
        consents['marketing']   = { status: gcmGranted('adStorage') };
        consents['advertising'] = { status: gcmGranted('adStorage') };
        consents['analytics']   = { status: gcmGranted('analyticsStorage') };
        consents['functional']  = { status: gcmGranted('functionalityStorage') || true };
        consents['essential']   = { status: true };
      }

      const categories: any[] = [];
      const seenCategories = new Set<string>();
      let hasInteracted = !!gcm;

      (services ?? []).forEach((service) => {
        // Service shape on this build: { name, consent: boolean }.
        // Older builds: { categorySlug, consent: { status, history } }.
        const status = service?.consent === true || service?.consent?.status === true;
        const categorySlug = (service?.categorySlug ?? service?.category ?? '').toString().toLowerCase();

        if (categorySlug && !gcm) {
          if (consents[categorySlug]?.status !== true) {
            consents[categorySlug] = { status };
          }
          if (!seenCategories.has(categorySlug)) {
            seenCategories.add(categorySlug);
            categories.push({ key: categorySlug, slug: categorySlug, status });
          }
        }
        if (Array.isArray(service?.consent?.history) && service.consent.history.length > 0) {
          hasInteracted = true;
        }
      });

      if (gcm) {
        // Surface the gcm-derived categories so downstream consumers see them.
        ['marketing', 'advertising', 'analytics', 'functional', 'essential'].forEach((slug) => {
          if (!seenCategories.has(slug) && consents[slug]) {
            seenCategories.add(slug);
            categories.push({ key: slug, slug, status: consents[slug].status });
          }
        });
      }

      if (!hasInteracted && ui && typeof ui.isConsentRequired === 'function') {
        try {
          hasInteracted = ui.isConsentRequired() === false;
        } catch {
          // ignore — leave hasInteracted as is.
        }
      }

      return { consents, categories, services: services ?? [], gcm, hasInteracted };
    } catch (error) {
      if (!this.consentReadErrorLogged) {
        this.consentReadErrorLogged = true;
        console.warn('Error retrieving Usercentrics consent data:', error);
      }

      return null;
    }
  }

  hasConsentForCategory(categoryId: string): boolean {
    const consentData = this.getConsentData();
    if (!consentData) {
      return false;
    }

    return consentData.consents?.[categoryId]?.status === true;
  }

  initializeUsercentrics(): void {
    if (!isPlatformBrowser(this.platformId) || !this.isEnabled) {
      return;
    }

    this.loadScript(usercentricsCmpScriptId, usercentricsCmpSrc, {
      'data-settings-id': this.settingsId
    });

    if (environment.usercentrics.autoblockerEnabled) {
      this.loadScript(usercentricsAutoblockerScriptId, usercentricsAutoblockerSrc);
    }

    this.registerConsentBridge();
  }

  isServiceConsented(serviceName: string): boolean {
    const consentData = this.getConsentData();
    const services = consentData?.services;
    if (!Array.isArray(services)) {
      return false;
    }

    const target = serviceName.trim().toLowerCase();
    return services.some((s: any) => {
      const name = (s?.name ?? '').toString().trim().toLowerCase();
      const status = s?.consent === true || s?.consent?.status === true;
      return name === target && status;
    });
  }

  onConsentChange(callback: (consentData: any) => void): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const dispatch = (event: any) => {
      const consentData = this.resolveConsentData(event);
      callback(consentData);
    };

    // V3 Web CMP fires window-level events. Downstream consumers
    // (loadBlueConic, loadPingVp, appendVwoSmartCode) are idempotent so
    // duplicate dispatches are safe.
    window.addEventListener('UC_CONSENT', dispatch);
    window.addEventListener(usercentricsConsentChangeEvent, dispatch);
  }

  onUsercentricsInitialized(callback: () => void): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const alreadyInitialised =
      !!(window as any).UC_UI || this.hasPersistedConsentDecision();

    if (alreadyInitialised) {
      Promise.resolve().then(() => {
        try {
          callback();
        } catch (error) {
          console.warn('Usercentrics initialized callback error:', error);
        }
      });
    }

    window.addEventListener(usercentricsInitializedEvent, callback);
  }

  pushConsentToGTM(consentState: Record<string, 'granted' | 'denied'>, consentData?: any): void {
    if (window.dataLayer) {
      window.dataLayer.push({
        event: 'consent_update',
        consent: consentState,
        usercentricsConsentData: consentData ?? null,
        source: 'usercentrics'
      });
    }
  }

  showConsentBanner(): void {
    if (!isPlatformBrowser(this.platformId) || !window.UC_UI) {
      return;
    }

    try {
      if (typeof window.UC_UI.showFirstLayer === 'function') {
        window.UC_UI.showFirstLayer();
      }
    } catch (error) {
      console.warn('Error showing consent banner:', error);
    }
  }

  waitForConsentDecision(): Promise<void> {
    if (!isPlatformBrowser(this.platformId) || !this.isEnabled) {
      return Promise.resolve();
    }

    // Fast path: if the user already made a decision on a previous visit, the
    // Usercentrics SDK persists uc_user_interaction = "true" in localStorage /
    // cookies. We can resolve immediately without waiting for the SDK to load.
    if (this.hasPersistedConsentDecision()) {
      return Promise.resolve();
    }

    if (this.consentDecisionPromise) {
      return this.consentDecisionPromise;
    }

    this.consentDecisionPromise = new Promise<void>((resolve) => {
      let resolved = false;
      let interactionListenersAttached = false;
      // Tracks the CMP's current view so the watchdog does not fight the user when they open the second-layer settings panel like ("Meer informatie").
      let currentView = 'NONE';
      const cleanup: (() => void)[] = [];

      const safeResolve = () => {
        if (resolved) {
          return;
        }

        resolved = true;
        cleanup.forEach((fn) => fn());
        this.consentDecisionPromise = null;
        resolve();
      };

      const isConsentAlreadyGiven = (): boolean => {
        // V3 Web CMP persists the consent record under localStorage['ucData']
        // (and on older builds writes uc_user_interaction = 'true') the moment
        // the user clicks Accept, Deny or Save. Both survive reloads and are
        // set BEFORE UC_UI finishes re-initialising on the next visit.
        if (this.hasPersistedConsentDecision()) {
          return true;
        }

        const ui = window.UC_UI;
        if (!ui) {
          return false;
        }

        // V3: isConsentRequired() === true means the user still needs to decide;
        // false means a decision already exists.
        if (typeof ui.isConsentRequired === 'function') {
          try {
            return ui.isConsentRequired() === false;
          } catch {
            // Fall back to consent data shape checks below.
          }
        }

        const consentData = this.getConsentData();
        if (!consentData) {
          return false;
        }

        if (consentData.hasInteracted === true) {
          return true;
        }

        const consents = consentData.consents;
        if (!consents || typeof consents !== 'object') {
          return false;
        }

        return Object.values(consents).some((consent: any) => {
          if (typeof consent === 'boolean') {
            return true;
          }

          return typeof consent?.status === 'boolean';
        });
      };

      // Resolve on any consent decision. The Usercentrics V3 Web CMP
      // (loader.js from web.cmp.usercentrics.eu) dispatches:
      //   - UC_UI_VIEW_CHANGED with detail.view === 'NONE' when the banner closes
      //   - UC_CONSENT (window-level) and onConsentUpdate (SDK-level) when consent is recorded
      const attachInteractionListeners = () => {
        if (interactionListenersAttached) {
          return;
        }

        interactionListenersAttached = true;
        const onInteraction = () => safeResolve();

        // Track the current view so the watchdog can decide whether the banner
        // truly needs re-asserting. SDK fires UC_UI_VIEW_CHANGED with:
        //   - 'FIRST_LAYER'  → banner visible
        //   - 'SECOND_LAYER' → user opened settings panel (do NOT re-show first layer!)
        //   - 'NONE'         → banner hidden (either auto-close or after a decision)
        const onViewChanged = (event: any) => {
          const newView = event?.detail?.view;
          if (typeof newView === 'string') {
            currentView = newView;
          }

          if (newView !== 'NONE') {
            return;
          }

          if (isConsentAlreadyGiven()) {
            safeResolve();
          } else {
            // Banner was hidden without a decision — re-open it.
            this.showConsentBanner();
          }
        };
        window.addEventListener('UC_UI_VIEW_CHANGED', onViewChanged);
        cleanup.push(() => window.removeEventListener('UC_UI_VIEW_CHANGED', onViewChanged));

        window.addEventListener('UC_CONSENT', onInteraction, { once: true });
        cleanup.push(() => window.removeEventListener('UC_CONSENT', onInteraction));

        window.addEventListener(usercentricsConsentChangeEvent, onInteraction, { once: true });
        cleanup.push(() => window.removeEventListener(usercentricsConsentChangeEvent, onInteraction));
      };

      const onUCReady = () => {
        if (isConsentAlreadyGiven()) {
          safeResolve();
          return;
        }

        // Banner is showing — wait for user to respond.
        attachInteractionListeners();

        // Do not force-show immediately. Some browsers expose UC_UI before
        // consent storage is fully hydrated; the watchdog below will show the
        // banner on the next tick if consent is truly still missing.
      };

      // The UC_UI global is created early but its consent state (loaded from
      // cookies/storage) is populated asynchronously. We must only act once the
      // SDK signals it's fully initialized — otherwise isConsentAlreadyGiven()
      // returns false on a returning visitor and we incorrectly re-show the banner.
      const isUCFullyInitialized = (): boolean => {
        const ui = window.UC_UI;
        if (!ui) {
          return false;
        }

        if (typeof ui.isInitialized === 'function') {
          try {
            return ui.isInitialized() === true;
          } catch {
            // Fall through to the next check
          }
        }

        // Works for both V2 (native getConsentData) and V3 (synthesized from
        // getServicesBaseInfo/getServicesFullInfo by our adapter).
        const consentData = this.getConsentData();
        if (consentData && typeof consentData === 'object') {
          return consentData.hasInteracted === true ||
            (consentData.consents && typeof consentData.consents === 'object') ||
            Array.isArray(consentData.services);
        }

        return false;
      };

      const checkConsentState = () => {
        if (isUCFullyInitialized()) {
          onUCReady();
        }
      };

      checkConsentState();

      const onInitialized = () => {
        checkConsentState();
      };

      window.addEventListener(usercentricsInitializedEvent, onInitialized, { once: true });
      cleanup.push(() => {
        window.removeEventListener(usercentricsInitializedEvent, onInitialized);
      });

      // Keep polling until the SDK is fully initialized and consent state is known.
      // Once onUCReady has run, it either resolves (consent exists) or shows the banner.
      const readinessCheckId = window.setInterval(checkConsentState, 250);
      cleanup.push(() => window.clearInterval(readinessCheckId));

      // Defensive watchdog: some CMP versions auto-close the banner shortly after
      // initialization without firing a reliable event. Re-assert the banner on
      // every tick until consent is recorded — but ONLY when the CMP view is
      // 'NONE' (truly hidden). If the user has opened the second-layer settings
      // panel, leave them alone so they can change preferences.
      const bannerWatchdogId = window.setInterval(() => {
        if (resolved || !isUCFullyInitialized()) {
          return;
        }

        if (isConsentAlreadyGiven()) {
          safeResolve();
          return;
        }

        if (currentView === 'NONE') {
          this.showConsentBanner();
        }
      }, 500);
      cleanup.push(() => window.clearInterval(bannerWatchdogId));
    });

    return this.consentDecisionPromise;
  }

  private extractConsentDataFromEvent(event: any): any {
    const detail = event?.detail;
    if (!detail) {
      return null;
    }

    if (detail.consents || detail.categories || detail.services || detail.hasInteracted === true) {
      return detail;
    }

    if (detail.consentData && typeof detail.consentData === 'object') {
      return detail.consentData;
    }

    if (detail.data && typeof detail.data === 'object') {
      return detail.data;
    }

    return null;
  }

  private extractServicesArray(ucData: any): any[] | null {
    const candidates: any[] = [
      ucData?.consent?.services,
      ucData?.consents?.services,
      ucData?.services
    ];
    for (const candidate of candidates) {
      if (!candidate) {
        continue;
      }
      if (Array.isArray(candidate) && candidate.length > 0 && this.looksLikeServices(candidate)) {
        return candidate;
      }
      if (typeof candidate === 'object' && !Array.isArray(candidate)) {
        const values = Object.values(candidate).filter((v) => v && typeof v === 'object');
        if (values.length > 0 && this.looksLikeServices(values)) {
          return values;
        }
      }
    }
    return null;
  }

  private hasConsentForAnyCategory(consentData: any, categories: string[]): boolean {
    const targets = categories.map((category) => category.toLowerCase());
    const matchesCategory = (value: any): boolean => {
      const categoryHint = `${value?.key ?? ''} ${value?.name ?? ''} ${value?.slug ?? ''} ${value?.category ?? ''} ${value?.categorySlug ?? ''}`.toLowerCase();
      return targets.some((target) => categoryHint.indexOf(target) !== -1);
    };

    const consents = consentData?.consents;
    if (consents && typeof consents === 'object') {
      for (const category of targets) {
        if (this.isConsentGranted(consents[category])) {
          return true;
        }
      }

      const consentValues = Object.values(consents as Record<string, any>);
      if (consentValues.some((value) => matchesCategory(value) && this.isConsentGranted(value))) {
        return true;
      }
    }

    if (Array.isArray(consentData?.categories)) {
      if (consentData.categories.some((value: any) => matchesCategory(value) && this.isConsentGranted(value))) {
        return true;
      }
    }

    if (Array.isArray(consentData?.services)) {
      if (consentData.services.some((value: any) => matchesCategory(value) && this.isConsentGranted(value))) {
        return true;
      }
    }

    return false;
  }

  private hasPersistedConsentDecision(): boolean {
    if (!isPlatformBrowser(this.platformId)) {
      return false;
    }

    try {
      // V3 Web CMP writes the full consent record under 'ucData' as soon as
      // the user makes a decision. Presence of a non-empty value is the
      // canonical signal on this build.
      if (typeof localStorage !== 'undefined') {
        const ucData = localStorage.getItem('ucData');
        if (ucData && ucData !== 'null' && ucData !== '{}' && ucData !== '[]') {
          return true;
        }
      }
    } catch {
      // ignore — private mode / disabled storage
    }

    try {
      if (typeof localStorage !== 'undefined' &&
          localStorage.getItem('uc_user_interaction') === 'true') {
        return true;
      }
    } catch {
    }

    try {
      if (typeof document !== 'undefined' &&
          document.cookie.indexOf('uc_user_interaction=true') !== -1) {
        return true;
      }
    } catch {
      // ignore
    }

    try {
      if (typeof localStorage !== 'undefined') {
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (!key) {
            continue;
          }

          // ucString) as well as Usercentrics variants (usercentrics_*).
          if (!/^uc/i.test(key)) {
            continue;
          }

          const value = localStorage.getItem(key);
          if (!value || value === 'null' || value === '{}' || value === '[]') {
            continue;
          }

          // Heuristic: a stored consent record always contains at least one
          // of these markers. Avoids false-positives from empty placeholders.
          if (value.indexOf('"consent"') !== -1 ||
              value.indexOf('"consents"') !== -1 ||
              value.indexOf('"consentStatus"') !== -1 ||
              value.indexOf('"controllerId"') !== -1 ||
              value.indexOf('"services"') !== -1 ||
              value.indexOf('"action"') !== -1) {
            return true;
          }
        }
      }
    } catch {
      // ignore
    }

    return false;
  }

  private isConsentGranted(value: any): boolean {
    if (value === true) {
      return true;
    }

    if (!value || typeof value !== 'object') {
      return false;
    }

    if (typeof value.status === 'boolean') {
      return value.status;
    }

    if (typeof value.consentStatus === 'boolean') {
      return value.consentStatus;
    }

    if (typeof value.granted === 'boolean') {
      return value.granted;
    }

    return false;
  }

  private loadScript(id: string, src: string, attributes: Record<string, string> = {}): void {
    if (document.getElementById(id)) {
      return;
    }

    const script = document.createElement('script');
    script.id = id;
    script.src = src;
    script.type = 'text/javascript';

    Object.entries(attributes).forEach(([key, value]) => {
      script.setAttribute(key, value);
    });

    document.head.appendChild(script);
  }

  private looksLikeServices(values: any[]): boolean {
    return values.some((v: any) =>
      v && typeof v === 'object' &&
      (v.consent !== undefined || v.categorySlug !== undefined || v.category !== undefined || v.name !== undefined)
    );
  }

  private mapToGoogleConsentState(consentData: any): Record<string, 'granted' | 'denied'> {
    // Prefer the gcm block written by Usercentrics V3 — it already encodes
    // Google Consent Mode v2 keys (camelCase). Fall back to category-based
    // aggregation for older builds.
    const gcm = consentData?.gcm;
    if (gcm && typeof gcm === 'object') {
      const toState = (value: any): 'granted' | 'denied' =>
        value === 'granted' ? 'granted' : 'denied';
      return {
        ad_storage: toState(gcm.adStorage),
        ad_user_data: toState(gcm.adUserData ?? gcm.adStorage),
        ad_personalization: toState(gcm.adPersonalization ?? gcm.adStorage),
        analytics_storage: toState(gcm.analyticsStorage),
        functionality_storage: toState(gcm.functionalityStorage ?? 'granted'),
        personalization_storage: toState(gcm.personalizationStorage ?? gcm.adPersonalization ?? gcm.adStorage),
        security_storage: 'granted'
      };
    }

    const advertisingGranted = this.hasConsentForAnyCategory(consentData, ['advertising', 'marketing']);
    const analyticsGranted = this.hasConsentForAnyCategory(consentData, ['analytics']);
    const functionalityGranted = this.hasConsentForAnyCategory(consentData, ['functionality']);
    const personalizationGranted = this.hasConsentForAnyCategory(consentData, ['marketing', 'advertising']);

    return {
      ad_storage: advertisingGranted ? 'granted' : 'denied',
      ad_user_data: advertisingGranted ? 'granted' : 'denied',
      ad_personalization: advertisingGranted ? 'granted' : 'denied',
      analytics_storage: analyticsGranted ? 'granted' : 'denied',
      functionality_storage: functionalityGranted ? 'granted' : 'denied',
      personalization_storage: personalizationGranted ? 'granted' : 'denied',
      security_storage: 'granted'
    };
  }

  private readUcData(): any | null {
    try {
      if (typeof localStorage === 'undefined') {
        return null;
      }
      const raw = localStorage.getItem('ucData');
      if (!raw || raw === 'null' || raw === '{}' || raw === '[]') {
        return null;
      }
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }

  private registerConsentBridge(): void {
    if (this.consentListenerAttached) {
      return;
    }

    const handler = (event: any) => {
      const consentData = this.resolveConsentData(event);
      const mappedConsent = this.mapToGoogleConsentState(consentData);

      if (typeof window.gtag === 'function') {
        window.gtag('consent', 'update', mappedConsent);
      }

      this.pushConsentToGTM(mappedConsent, consentData);
    };

    // V3 Web CMP dispatches window-level events. Both UC_CONSENT and
    // onConsentUpdate are documented; we listen to both to be tolerant of
    // future loader changes.
    window.addEventListener('UC_CONSENT', handler);
    window.addEventListener(usercentricsConsentChangeEvent, handler);

    this.consentListenerAttached = true;
  }

  private resolveConsentData(event?: any): any {
    return this.extractConsentDataFromEvent(event) ?? this.getConsentData() ?? {};
  }
}

declare global {
  interface Window {
    UC_UI?: any;
    dataLayer: any[];
    gtag?: Function;
  }
}
